// @ts-check
/**
 * Typed meta‑model enums and guards for the causal graph.
 * Keep string literals centralized to avoid scattering.
 */

export const NodeKinds = Object.freeze({
  VISION: 'vision',
  OBJECTIVE: 'objective',
  OUTCOME: 'outcome',
  KEY_RESULT: 'key_result',
  INITIATIVE: 'initiative',
  STAKEHOLDER: 'stakeholder',
  BEHAVIOR: 'behavior',
  DELIVERABLE: 'deliverable',
  LEVER: 'lever',
  COMPONENT: 'component',
  RISK: 'risk',
  EVIDENCE: 'evidence',
  DEFAULT: 'default'
});

export const EdgeKinds = Object.freeze({
  DRIVES: 'drives',
  INFLUENCES: 'influences',
  ENACTED_BY: 'enacted_by',
  REQUIRES: 'requires',
  PRODUCES: 'produces',
  AFFECTS: 'affects',
  TAGGED_AS: 'tagged_as',
  PART_OF: 'part_of',
  BLOCKED_BY: 'blocked_by',
  DEPENDS_ON: 'depends_on'
});

// Allowed pairs for each edge kind (fromKind -> toKind)
export const EDGE_RULES = Object.freeze({
  [EdgeKinds.DRIVES]: [[NodeKinds.OBJECTIVE, NodeKinds.KEY_RESULT]],
  [EdgeKinds.INFLUENCES]: [
    [NodeKinds.INITIATIVE, NodeKinds.KEY_RESULT],
    [NodeKinds.INITIATIVE, NodeKinds.OUTCOME]
  ],
  [EdgeKinds.ENACTED_BY]: [[NodeKinds.BEHAVIOR, NodeKinds.STAKEHOLDER]], // reversed arrow in meta; canvas may show Stakeholder -> Behavior
  [EdgeKinds.REQUIRES]: [
    [NodeKinds.DELIVERABLE, NodeKinds.COMPONENT],
    [NodeKinds.BEHAVIOR, NodeKinds.DELIVERABLE]
  ],
  [EdgeKinds.PRODUCES]: [[NodeKinds.INITIATIVE, NodeKinds.DELIVERABLE]],
  [EdgeKinds.AFFECTS]: [[NodeKinds.BEHAVIOR, NodeKinds.KEY_RESULT]],
  [EdgeKinds.TAGGED_AS]: [
    [NodeKinds.KEY_RESULT, NodeKinds.LEVER],
    [NodeKinds.INITIATIVE, NodeKinds.LEVER]
  ],
  [EdgeKinds.PART_OF]: [
    [NodeKinds.KEY_RESULT, NodeKinds.COMPONENT],
    [NodeKinds.INITIATIVE, NodeKinds.COMPONENT],
    [NodeKinds.COMPONENT, NodeKinds.COMPONENT]
  ],
  [EdgeKinds.BLOCKED_BY]: [[NodeKinds.INITIATIVE, NodeKinds.RISK]],
  [EdgeKinds.DEPENDS_ON]: [
    [NodeKinds.INITIATIVE, NodeKinds.INITIATIVE],
    [NodeKinds.COMPONENT, NodeKinds.COMPONENT]
  ]
});

/** Validate from/to kinds for an edge kind. */
export function isValidEdge(kind, fromKind, toKind) {
  const rules = EDGE_RULES[kind];
  if (!rules) return true; // be permissive for unknown kinds in P0
  return rules.some(([a, b]) => a === fromKind && b === toKind);
}

/** Default polarity by rule (Behavior→KR +; Deliverable→Behavior +; Initiative→KR ask/assume +). */
export function defaultPolarity(kind, fromKind, toKind, krName = '') {
  if (kind === EdgeKinds.AFFECTS && fromKind === NodeKinds.BEHAVIOR && toKind === NodeKinds.KEY_RESULT) return '+';
  if (kind === EdgeKinds.PRODUCES && fromKind === NodeKinds.INITIATIVE && toKind === NodeKinds.DELIVERABLE) return '+';
  if (kind === EdgeKinds.REQUIRES && fromKind === NodeKinds.DELIVERABLE && toKind === NodeKinds.COMPONENT) return '+';
  if (kind === EdgeKinds.INFLUENCES && fromKind === NodeKinds.INITIATIVE && (toKind === NodeKinds.KEY_RESULT || toKind === NodeKinds.OUTCOME)) return '+';
  // Heuristic: KR name with cost/time/time-to-… defaults negative when affected by behavior
  const lower = String(krName || '').toLowerCase();
  if ((lower.includes('cost') || lower.includes('time')) && fromKind === NodeKinds.BEHAVIOR) return '-';
  return '+';
}

export const DEFAULTS = Object.freeze({
  edge: {
    polarity: '+',
    strength: 0.5,
    confidence: 0.5,
    evidenceLevel: 'hypothesis'
  }
});
